import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Clock,
  MessageSquare,
  User,
  Building
} from 'lucide-react';
import { cn } from '../utils';
import emailjs from '@emailjs/browser';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  
  // 페이지 로드 시 상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact.errors.nameRequired', 'Name is required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.emailRequired', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid', 'Please enter a valid email address');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact.errors.subjectRequired', 'Subject is required');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.errors.messageRequired', 'Message is required');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('contact.errors.messageTooShort', 'Message must be at least 10 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createMailtoLink = (formData: typeof formData) => {
    const subject = `Contact Form: ${formData.subject}`;
    const body = `
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'N/A'}
Phone: ${formData.phone || 'N/A'}

Message:
${formData.message}
    `;
    
    const mailtoLink = `mailto:support@scanvcf.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    return mailtoLink;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    // EmailJS 설정 확인 및 디버깅
    const EMAILJS_SERVICE_ID = ((import.meta as any).env?.VITE_EMAILJS_SERVICE_ID as string | undefined) || 'service_66b6d9j';
    const EMAILJS_TEMPLATE_ID = ((import.meta as any).env?.VITE_EMAILJS_TEMPLATE_ID as string | undefined) || 'template_sy4lxjv';
    const EMAILJS_PUBLIC_KEY = ((import.meta as any).env?.VITE_EMAILJS_PUBLIC_KEY as string | undefined) || '3wyogp3HeNumDAdGP';

    console.log('🔧 [ContactPage] EmailJS Config:', {
      serviceId: EMAILJS_SERVICE_ID,
      templateId: EMAILJS_TEMPLATE_ID,
      publicKey: EMAILJS_PUBLIC_KEY ? `${EMAILJS_PUBLIC_KEY.substring(0, 8)}...` : undefined,
      hasEmailJS: typeof emailjs !== 'undefined'
    });

    try {
      if (!emailjs) {
        throw new Error('EmailJS is not loaded');
      }

      // EmailJS로 이메일 전송 시도
      console.log('📧 [ContactPage] Attempting to send email via EmailJS...');

      const templateParams = {
        name: formData.name,
        email: formData.email,
        company: formData.company || 'N/A',
        phone: formData.phone || 'N/A',
        subject: formData.subject,
        message: formData.message,
        to_email: 'support@scanvcf.com',
        reply_to: formData.email // 발신자 이메일을 reply-to로 설정
      };

      console.log('📧 [ContactPage] Template params being sent:', {
        ...templateParams,
        serviceId: EMAILJS_SERVICE_ID,
        templateId: EMAILJS_TEMPLATE_ID
      });

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      console.log('✅ [ContactPage] EmailJS full response:', JSON.stringify(result, null, 2));
      console.log('✅ [ContactPage] EmailJS send result:', result);

      if (result?.status === 200 || result?.text === 'OK') {
        console.log('✅ [ContactPage] Email sent successfully');
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: '',
          phone: ''
        });
        return;
      } else {
        console.warn('⚠️ [ContactPage] EmailJS returned unexpected result:', result);
        throw new Error('EmailJS returned unexpected result');
      }

    } catch (error) {
      console.error('❌ [ContactPage] EmailJS failed:', error);

      // Fallback 1: 백엔드 API가 있다면 시도
      try {
        console.log('🔄 [ContactPage] Trying fallback API...');
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (response.ok) {
          console.log('✅ [ContactPage] Fallback API success');
          setSubmitStatus('success');
          setFormData({
            name: '',
            email: '',
            company: '',
            subject: '',
            message: '',
            phone: ''
          });
          return;
        }
      } catch (apiError) {
        console.error('❌ [ContactPage] Fallback API failed:', apiError);
      }

      // Fallback 2: Gmail 웹 링크 열기
      try {
        console.log('🔄 [ContactPage] Trying Gmail fallback...');
        const subject = `Contact Form: ${formData.subject}`;
        const body = `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || 'N/A'}\nPhone: ${formData.phone || 'N/A'}\n\nMessage:\n${formData.message}`;
        const gmailLink = `https://mail.google.com/mail/?view=cm&to=support@scanvcf.com&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        console.log('📧 [ContactPage] Opening Gmail link:', gmailLink.substring(0, 100) + '...');
        window.open(gmailLink, '_blank');
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: '',
          phone: ''
        });
      } catch (gmailError) {
        console.error('❌ [ContactPage] Gmail fallback failed:', gmailError);
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="h-6 w-6" />,
      title: t('contact.info.email.title', 'Email'),
      content: 'support@scanvcf.com',
      description: t('contact.info.email.description', 'Send us an email anytime')
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="lux-ivory py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              {t('contact.hero.title', 'Get in Touch')}
            </h1>
            <p className="text-lg text-gray-500 max-w-3xl mx-auto leading-relaxed">
              {t('contact.hero.subtitle', 'Have questions about our digital business card platform? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="lux-card p-8">
              <div className="flex items-center mb-8">
                <div className="lux-icon-tile mr-4 !h-12 !w-12">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t('contact.form.title', 'Send us a Message')}
                  </h2>
                  <p className="text-gray-600">
                    {t('contact.form.subtitle', 'We\'ll get back to you within 24 hours')}
                  </p>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                    <div>
                      <p className="text-green-800 font-medium">
                        {t('contact.form.success', 'Thank you! Your message has been sent. We will respond within 24 hours.')}
                      </p>
                      <p className="text-green-700 text-sm mt-1">
                        {t('contact.form.successNote', 'If you haven\'t received a response within 24 hours, please check your spam folder or contact us directly at support@scanvcf.com')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                    <div>
                      <p className="text-red-800 font-medium">
                        {t('contact.form.error', 'Sorry, there was an error sending your message. Please try again.')}
                      </p>
                      <p className="text-red-700 text-sm mt-1">
                        {t('contact.form.errorNote', 'You can also send your message directly to support@scanvcf.com')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.name', 'Full Name')} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          setFormData(prev => ({ ...prev, [name]: value }));
                          if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
                        }}
                        className={cn(
                          "w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                          errors.name ? "border-red-300" : "border-gray-300"
                        )}
                        placeholder={t('contact.form.namePlaceholder', 'Enter your full name')}
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email', 'Email Address')} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          setFormData(prev => ({ ...prev, [name]: value }));
                          if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
                        }}
                        className={cn(
                          "w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                          errors.email ? "border-red-300" : "border-gray-300"
                        )}
                        placeholder={t('contact.form.emailPlaceholder', 'Enter your email address')}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.company', 'Company')}
                    </label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          setFormData(prev => ({ ...prev, [name]: value }));
                        }}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('contact.form.companyPlaceholder', 'Enter your company name')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.phone', 'Phone Number')}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          setFormData(prev => ({ ...prev, [name]: value }));
                        }}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('contact.form.phonePlaceholder', 'Enter your phone number')}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.subject', 'Subject')} *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setFormData(prev => ({ ...prev, [name]: value }));
                      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
                    }}
                    className={cn(
                      "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                      errors.subject ? "border-red-300" : "border-gray-300"
                    )}
                    placeholder={t('contact.form.subjectPlaceholder', 'What is this about?')}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text sm font-medium text-gray-700 mb-2">
                    {t('contact.form.message', 'Message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setFormData(prev => ({ ...prev, [name]: value }));
                      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
                    }}
                    className={cn(
                      "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none",
                      errors.message ? "border-red-300" : "border-gray-300"
                    )}
                    placeholder={t('contact.form.messagePlaceholder', 'Tell us how we can help you...')}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    "w-full flex items-center justify-center px-6 py-4 text-base font-semibold rounded-full transition-colors",
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed text-white"
                      : "lux-btn-gold"
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      {t('contact.form.sending', 'Sending...')}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-3" />
                      {t('contact.form.send', 'Send Message')}
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {t('contact.info.title', 'Contact Information')}
                </h2>
                <p className="text-gray-600 mb-8">
                  {t('contact.info.description', 'We\'re here to help and answer any question you might have. We look forward to hearing from you!')}
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="lux-icon-tile flex-shrink-0 !h-12 !w-12">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {info.title}
                      </h3>
                      <p className="text-gray-900 font-medium mb-1">
                        {info.content}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {info.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* FAQ Section */}
              <div className="lux-ivory border border-[#e8e6e1] rounded-2xl p-6">
                <h3 className="text-lg font-semibold tracking-tight text-gray-900 mb-4">
                  {t('contact.faq.title', 'Frequently Asked Questions')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">
                      {t('contact.faq.q1', 'How quickly do you respond?')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('contact.faq.a1', 'We typically respond within 24 hours during business days.')}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">
                      {t('contact.faq.q3', 'Can I schedule a demo?')}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {t('contact.faq.a3', 'Absolutely! Contact us to schedule a personalized demo.')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
